import { Injectable } from '@angular/core';
import { LeadsService } from '@services/api/leads.service';
import { ApiService } from '@services/api/api.service';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { DUPLICATE_LEAD_STATUS, requiredDefaultsFrom, toLeadUpdateBody } from '../helpers/merge.helpers';
import { customScalarField, DUPLICATE_MERGE_FIELDS } from '../models/lead-field-registry';
import {
  DuplicateLead,
  EntryMergeMode,
  FieldDefinition,
  LeadComparisonResult,
  MergeableFieldComparison,
  MergeAuditTrail,
  MergePayload,
  MergeResult,
  ScalarFieldComparison,
  ScalarSelection,
} from '../models/duplicate-lead-merge.types';

export interface BuildPayloadParams {
  keepLead: DuplicateLead;
  comparison: LeadComparisonResult;
  /** fieldKey -> chosen scalar selection */
  scalarSelection: Record<string, ScalarSelection>;
  /** fieldKey -> (entryId -> selected) */
  entrySelection: Record<string, Record<string, boolean>>;
  /** fieldKey -> merge (append) or overwrite the kept lead's own entries. Defaults to merge. */
  entryMode?: Record<string, EntryMergeMode>;
  /** every duplicate lead (kept lead excluded) that will be closed — full objects */
  duplicateLeads: DuplicateLead[];
  mergedBy?: string;
  mergedAt?: string;
}

/**
 * Turns the user's Step-2 selections into an executable {@link MergePayload}
 * and runs it. Nothing here overwrites a field the user didn't tick — the
 * kept-lead update is a *partial* body containing only approved keys.
 */
@Injectable()
export class LeadMergeService {
  private readonly fieldByKey = new Map<string, FieldDefinition>(DUPLICATE_MERGE_FIELDS.map(f => [f.key, f]));

  constructor(
    private readonly leads: LeadsService,
    private readonly api: ApiService
  ) {}

  private resolveField(key: string): FieldDefinition {
    return this.fieldByKey.get(key) ?? customScalarField(key);
  }

  // ---- pure payload construction -----------------------------------------

  buildPayload(params: BuildPayloadParams): MergePayload {
    const { keepLead, comparison, scalarSelection, entrySelection, entryMode, duplicateLeads } = params;
    const duplicateLeadIds = duplicateLeads.map(l => l._id);

    const keepUpdate: Record<string, any> = {};
    const audit: MergeAuditTrail = {
      merged_by: params.mergedBy,
      merged_at: params.mergedAt,
      keep_lead_id: keepLead._id,
      source_lead_ids: [...duplicateLeadIds],
      copied_fields: [],
      replaced_fields: [],
      merged_entries: [],
      duplicate_relationships: duplicateLeadIds.map(id => ({ lead_id: id, duplicate_of: keepLead._id })),
    };

    for (const scalar of comparison.scalars) {
      this.applyScalar(scalar, scalarSelection[scalar.key], keepLead, keepUpdate, audit);
    }
    for (const mergeable of comparison.mergeables) {
      this.applyMergeable(mergeable, entrySelection[mergeable.key] ?? {}, entryMode?.[mergeable.key] ?? 'merge', keepLead, keepUpdate, audit);
    }

    // Required-but-empty fields (enquiry_type / lead_source / lead_status) are
    // back-filled from whichever sibling lead has them, so the update validator
    // never 422s on a lead that was created without one.
    const requiredDefaults = requiredDefaultsFrom([keepLead, ...duplicateLeads]);

    // Full body = the whole kept lead with the approved changes applied — mirrors
    // how the edit-lead form PUTs the complete record (no partial document).
    const keepPayload = toLeadUpdateBody(keepLead, keepUpdate, requiredDefaults);

    // Duplicates are closed by moving them to the disqualified status and recording which lead they
    // were folded into, so they read the same way anywhere lead_status is shown or filtered.
    const duplicateUpdates = duplicateLeads.map(dup => ({
      leadId: dup._id,
      update: toLeadUpdateBody(dup, { status_type: DUPLICATE_LEAD_STATUS, duplicate_of: keepLead._id }, requiredDefaults),
    }));

    return { keepLeadId: keepLead._id, keepUpdate, keepPayload, duplicateUpdates, audit };
  }

  private applyScalar(
    scalar: ScalarFieldComparison,
    selection: ScalarSelection | undefined,
    keepLead: DuplicateLead,
    keepUpdate: Record<string, any>,
    audit: MergeAuditTrail
  ): void {
    if (!selection?.selected) return;
    const field = this.resolveField(scalar.key);
    Object.assign(keepUpdate, field.toPayload(selection.value, keepLead));

    const record = { key: scalar.key, label: scalar.label, from: selection.sourceId };
    if (scalar.status === 'empty') {
      audit.copied_fields.push({ ...record, value: selection.value });
    } else {
      audit.replaced_fields.push({ ...record, old_value: scalar.keepValue, new_value: selection.value });
    }
  }

  private applyMergeable(
    mergeable: MergeableFieldComparison,
    selectedMap: Record<string, boolean>,
    mode: EntryMergeMode,
    keepLead: DuplicateLead,
    keepUpdate: Record<string, any>,
    audit: MergeAuditTrail
  ): void {
    const chosen = mergeable.candidates.filter(c => selectedMap[c.id]);
    if (chosen.length === 0) return;

    const field = this.resolveField(mergeable.key);
    // merge     = the kept lead's own entries + the newly approved ones
    // overwrite = only the approved ones; the kept lead's entries are dropped
    const nextRaw = mode === 'overwrite' ? chosen.map(e => e.raw) : [...mergeable.existing.map(e => e.raw), ...chosen.map(e => e.raw)];
    Object.assign(keepUpdate, field.toPayload(nextRaw, keepLead));

    audit.merged_entries.push({ key: mergeable.key, label: mergeable.label, count: chosen.length, mode });
  }

  // ---- execution ----------------------------------------------------------

  /**
   * Executes the merge: partial-update the kept lead, flag every duplicate, then
   * best-effort persist the audit trail. Audit failure never fails the merge.
   */
  execute(payload: MergePayload): Observable<MergeResult> {
    // Only touch the kept lead when the user actually selected something to copy.
    const keep$ = Object.keys(payload.keepUpdate).length ? this.leads.updateLead(payload.keepLeadId, payload.keepPayload) : of(null);

    const dup$ = payload.duplicateUpdates.length ? forkJoin(payload.duplicateUpdates.map(d => this.leads.updateLead(d.leadId, d.update))) : of([]);

    return keep$.pipe(
      switchMap(() => dup$),
      switchMap(() =>
        this.saveAudit(payload.audit).pipe(
          map(() => ({
            keepLeadId: payload.keepLeadId,
            duplicateLeadIds: payload.duplicateUpdates.map(d => d.leadId),
            audit: payload.audit,
          }))
        )
      )
    );
  }

  /** Audit endpoint is optional/back-end-pending — swallow its failure. */
  private saveAudit(audit: MergeAuditTrail): Observable<any> {
    const withUser: MergeAuditTrail = { ...audit, merged_by: audit.merged_by ?? this.safeUserId() };
    return this.leads.saveDuplicateMergeAudit(withUser).pipe(catchError(() => of(null)));
  }

  private safeUserId(): string | undefined {
    try {
      return this.api.getUserId?.() ?? undefined;
    } catch {
      return undefined;
    }
  }
}
